'use client'

import { useEffect, useRef } from 'react'

export interface UseExitIntentOptions {
  onExitIntent: () => void
  enabled?: boolean
  sensitivity?: number
  delay?: number
}

/**
 * Hook to detect when user intends to leave the page (mouse moving toward browser chrome)
 * @param options Configuration options
 * @param options.onExitIntent Callback to fire when exit intent is detected
 * @param options.enabled Whether the hook is enabled (default: true)
 * @param options.sensitivity Pixels from top to trigger (default: 30)
 * @param options.delay Minimum delay in ms before exit intent can fire again (default: 1000)
 */
export const useExitIntent = ({
  onExitIntent,
  enabled = true,
  sensitivity = 30,
  delay = 1000,
}: UseExitIntentOptions) => {
  const hasTriggeredRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const onExitIntentRef = useRef(onExitIntent)

  // Keep callback ref updated without causing effect to re-run
  useEffect(() => {
    onExitIntentRef.current = onExitIntent
  }, [onExitIntent])

  useEffect(() => {
    if (!enabled) return

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse is moving upward and near the top of the viewport
      // and if we haven't triggered recently
      if (
        e.clientY <= sensitivity &&
        e.relatedTarget === null &&
        !hasTriggeredRef.current
      ) {
        hasTriggeredRef.current = true
        onExitIntentRef.current()

        // Reset after delay
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          hasTriggeredRef.current = false
        }, delay)
      }
    }

    document.addEventListener('mouseout', handleMouseLeave)

    return () => {
      document.removeEventListener('mouseout', handleMouseLeave)
      // Clear timeout on unmount to prevent memory leaks
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [enabled, sensitivity, delay])
}
